// ============================================
// AuthModal.jsx - Authentication Modal
// ============================================
// Modal for sign in and sign up
// Switches between modes, handles form submission

import React, { useState, useCallback } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Sparkles, HardDrive, Smartphone } from 'lucide-react';
import GhostLogo from './GhostLogo';

// ============================================
// SIGN IN FORM
// ============================================
function SignInForm({ onSubmit, onSwitchMode, loading, error }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(email, password);
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <div className="auth-header">
        <GhostLogo size={48} animated />
        <h2>Welcome Back</h2>
        <p>Sign in to continue chatting</p>
      </div>

      {error && <div className="auth-error">{error}</div>}

      <div className="form-group">
        <label htmlFor="signin-email">Email</label>
        <div className="input-wrapper">
          <Mail size={18} />
          <input
            id="signin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={loading}
            autoComplete="email"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="signin-password">Password</label>
        <div className="input-wrapper">
          <Lock size={18} />
          <input
            id="signin-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            disabled={loading}
            autoComplete="current-password"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      <button type="submit" className="auth-submit" disabled={loading}>
        {loading ? 'Signing in...' : 'Sign In'}
      </button>

      <div className="auth-footer">
        <p>
          Don't have an account?{' '}
          <button type="button" onClick={() => onSwitchMode('signup')}>
            Sign up
          </button>
        </p>
      </div>
    </form>
  );
}

// ============================================
// SIGN UP FORM
// ============================================
function SignUpForm({ onSubmit, onSwitchMode, loading, error }) {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError('');

    if (password !== password2) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    onSubmit({ email, username: username || undefined, password, password2 });
  };

  const displayError = localError || error;

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <div className="auth-header">
        <GhostLogo size={48} animated />
        <h2>Create Account</h2>
        <p>Get more messages and save your chats</p>
      </div>

      {displayError && <div className="auth-error">{displayError}</div>}

      <div className="form-group">
        <label htmlFor="signup-email">
          Email <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <Mail size={18} />
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={loading}
            autoComplete="email"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="signup-username">
          Username <span className="optional">(optional)</span>
        </label>
        <div className="input-wrapper">
          <User size={18} />
          <input
            id="signup-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="ghostwriter"
            disabled={loading}
            autoComplete="username"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="signup-password">
          Password <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <Lock size={18} />
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            minLength={8}
            disabled={loading}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <span className="hint">At least 8 characters</span>
      </div>

      <div className="form-group">
        <label htmlFor="signup-password2">
          Confirm Password <span className="required">*</span>
        </label>
        <div className="input-wrapper">
          <Lock size={18} />
          <input
            id="signup-password2"
            type={showPassword ? 'text' : 'password'}
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            placeholder="••••••••"
            required
            disabled={loading}
            autoComplete="new-password"
          />
        </div>
      </div>

      <button type="submit" className="auth-submit" disabled={loading}>
        {loading ? 'Creating account...' : 'Create Account'}
      </button>

      <div className="auth-benefits">
        <div className="benefit">
          <Sparkles size={18} />
          <span>More messages</span>
        </div>
        <div className="benefit">
          <HardDrive size={18} />
          <span>Save history</span>
        </div>
        <div className="benefit">
          <Smartphone size={18} />
          <span>Sync devices</span>
        </div>
      </div>

      <div className="auth-footer">
        <p>
          Already have an account?{' '}
          <button type="button" onClick={() => onSwitchMode('signin')}>
            Sign in
          </button>
        </p>
      </div>
    </form>
  );
}

// ============================================
// MAIN MODAL COMPONENT
// ============================================
export default function AuthModal({
  isOpen,
  onClose,
  mode = 'signin',
  onModeChange,
  onLogin,
  onRegister,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = useCallback(
    async (email, password) => {
      setLoading(true);
      setError('');

      const result = await onLogin(email, password);

      setLoading(false);

      if (result.success) {
        onSuccess?.();
      } else {
        setError(result.error || 'Login failed');
      }
    },
    [onLogin, onSuccess]
  );

  const handleRegister = useCallback(
    async (data) => {
      setLoading(true);
      setError('');

      const result = await onRegister(data);

      setLoading(false);

      if (result.success) {
        onSuccess?.();
      } else {
        setError(result.error || 'Registration failed');
      }
    },
    [onRegister, onSuccess]
  );

  const handleSwitchMode = useCallback(
    (newMode) => {
      setError('');
      onModeChange(newMode);
    },
    [onModeChange]
  );

  if (!isOpen) return null;

  return (
    <div className="auth-modal-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose}>
          <X size={20} />
        </button>

        {mode === 'signin' ? (
          <SignInForm
            onSubmit={handleLogin}
            onSwitchMode={handleSwitchMode}
            loading={loading}
            error={error}
          />
        ) : (
          <SignUpForm
            onSubmit={handleRegister}
            onSwitchMode={handleSwitchMode}
            loading={loading}
            error={error}
          />
        )}
      </div>

      <style>{`
        .auth-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .auth-modal {
          position: relative;
          background: var(--gi-gray-900, #111827);
          border: 1px solid var(--gi-gray-800, #1f2937);
          border-radius: 16px;
          width: 100%;
          max-width: 400px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .auth-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: transparent;
          border: none;
          color: var(--gi-gray-500, #6b7280);
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          transition: all 0.2s;
          z-index: 10;
        }

        .auth-close:hover {
          background: var(--gi-gray-800, #1f2937);
          color: var(--gi-gray-300, #d1d5db);
        }

        .auth-form {
          padding: 32px;
        }

        .auth-header {
          text-align: center;
          margin-bottom: 24px;
        }

        .auth-header h2 {
          color: var(--gi-gray-100, #f3f4f6);
          font-size: 24px;
          margin: 16px 0 8px;
        }

        .auth-header p {
          color: var(--gi-gray-500, #6b7280);
          margin: 0;
          font-size: 14px;
        }

        .auth-error {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
          padding: 12px;
          border-radius: 8px;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .form-group label {
          display: block;
          color: var(--gi-gray-300, #d1d5db);
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .form-group .required {
          color: #ef4444;
        }

        .form-group .optional {
          color: var(--gi-gray-600, #4b5563);
          font-weight: normal;
        }

        .form-group .hint {
          display: block;
          color: var(--gi-gray-600, #4b5563);
          font-size: 12px;
          margin-top: 4px;
        }

        .input-wrapper {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--gi-gray-800, #1f2937);
          border: 1px solid var(--gi-gray-700, #374151);
          border-radius: 8px;
          padding: 0 12px;
          transition: all 0.2s;
        }

        .input-wrapper:focus-within {
          border-color: var(--gi-green-600, #16a34a);
          box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
        }

        .input-wrapper svg {
          color: var(--gi-gray-500, #6b7280);
          flex-shrink: 0;
        }

        .input-wrapper input {
          flex: 1;
          background: transparent;
          border: none;
          color: var(--gi-gray-100, #f3f4f6);
          padding: 12px 0;
          font-size: 15px;
          outline: none;
        }

        .input-wrapper input::placeholder {
          color: var(--gi-gray-600, #4b5563);
        }

        .toggle-password {
          background: transparent;
          border: none;
          color: var(--gi-gray-500, #6b7280);
          cursor: pointer;
          padding: 4px;
        }

        .toggle-password:hover {
          color: var(--gi-gray-300, #d1d5db);
        }

        .auth-submit {
          width: 100%;
          background: linear-gradient(135deg, var(--gi-green-600, #16a34a) 0%, var(--gi-green-700, #15803d) 100%);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 14px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 8px;
        }

        .auth-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 20px rgba(22, 163, 74, 0.3);
        }

        .auth-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-benefits {
          display: flex;
          justify-content: center;
          gap: 24px;
          margin: 20px 0;
          padding: 16px 0;
          border-top: 1px solid var(--gi-gray-800, #1f2937);
          border-bottom: 1px solid var(--gi-gray-800, #1f2937);
        }

        .benefit {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--gi-gray-500, #6b7280);
        }

        .benefit svg {
          color: var(--gi-green-500, #22c55e);
        }

        .auth-footer {
          text-align: center;
          margin-top: 16px;
        }

        .auth-footer p {
          color: var(--gi-gray-500, #6b7280);
          font-size: 14px;
          margin: 0;
        }

        .auth-footer button {
          background: transparent;
          border: none;
          color: var(--gi-green-500, #22c55e);
          cursor: pointer;
          font-weight: 600;
          font-size: inherit;
        }

        .auth-footer button:hover {
          text-decoration: underline;
        }

        @media (max-width: 480px) {
          .auth-form {
            padding: 24px;
          }

          .auth-benefits {
            flex-direction: column;
            gap: 8px;
          }

          .benefit {
            flex-direction: row;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}
